import React, { useEffect } from 'react';
import { motion, useAnimationControls } from 'framer-motion';
import { cn } from '../../../lib/utils';
import { getThemeClass } from '../../../lib/theme';
import { SectionProps } from './types';
import { SectionContainer } from './SectionContainer';
import { fadeInUp, withReducedMotion } from '../../../lib/animations/variants';

const Section: React.FC<SectionProps> = ({
  children,
  id,
  className,
  background = 'white',
  width = 'default',
  spacing = 'md',
  animate = false,
  withOverlay = false,
  withDivider = false
}) => {
  const controls = useAnimationControls();
  
  useEffect(() => {
    if (animate) {
      // Ensure animation runs on mount and after route changes
      controls.set("initial");
      controls.start("animate");
    }
  }, [animate, controls]);

  const backgroundStyles = {
    white: getThemeClass('colors.background.main'),
    light: getThemeClass('colors.background.light'),
    dark: getThemeClass('colors.background.dark'),
    gradient: getThemeClass('gradients.page.main'),
    primary: getThemeClass('gradients.hero'),
    secondary: getThemeClass('gradients.page.main')
  };

  const spacingStyles = {
    none: '',
    sm: 'py-8',
    md: 'py-16',
    lg: 'py-24'
  };

  const content = (
    <section
      id={id}
      className={cn(
        'relative',
        backgroundStyles[background],
        spacingStyles[spacing],
        className
      )}
    >
      {withOverlay && (
        <div className={cn(
          "absolute inset-0 opacity-10",
          getThemeClass('overlays.dotMatrix')
        )} />
      )}
      <div className="relative">
        <SectionContainer width={width}>
          {children}
        </SectionContainer>
      </div>

      {withDivider && (
        <div className={cn(
            "h-16 overflow-hidden relative",
            getThemeClass('dividers.section'), 
        )}>
            <div className={cn(
            'absolute inset-0 transform',
            background === 'white' 
                ? getThemeClass('gradients.page.divider.light')
                : getThemeClass('gradients.page.divider.dark')
            )}>
            <div className={cn(
                "absolute inset-0",
                getThemeClass('overlays.pattern')
            )} />
            </div>
        </div>
        )}
    </section>
  );

  if (!animate) {
    return content;
  }

  return (
    <motion.div
      initial="initial"
      animate={controls}
      variants={withReducedMotion(fadeInUp)}
    >
      {content}
    </motion.div>
  );
};

export default Section;
